import * as React from 'react'
import { ErrorMessage } from '@lightspeed/design-system-react'
import classNames from 'classnames'
import { useT } from '@transifex/react'
import {
  CONTACT_OPTIONS,
  CustomerInfo,
  CUSTOMER_NAME_FIELD,
  PartialCustomerGroup,
} from '../../../common/types'
import {
  getDefaultContactOption,
  isInvalidEmail,
} from '../../../common/customer-contact-utils'
import { ContactNumber } from '../../../common/ContactNumber'
import { DottMessage } from '../../../common/DottMessage'
import { useCustomerGroups } from '../../../common/hooks/useCustomerGroups'
import { CustomerGroupsDropdown } from '../../../common/CustomerGroupsDropdown'
export interface Props {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  newCustomer: CustomerInfo | null
}

export const AddNewCustomerView: React.FC<Props> = ({
  onChange,
  newCustomer,
}) => {
  const t = useT()

  const addNewCustomerCopies = {
    firstName: t('First name', {
      _context: 'Input label for customer step in service modal.',
    }),
    firstNamePlaceholder: t('Enter first name', {
      _context: 'Input placeholder text for customer step in service modal.',
    }),
    lastName: t('Last name', {
      _context: 'Input label for customer step in service modal.',
    }),
    lastNamePlaceholder: t('Enter last name', {
      _context: 'Input placeholder text for customer step in service modal.',
    }),
    email: t('Email', {
      _context: 'Input label for customer step in service modal.',
    }),
    emailPlaceholder: t('Enter email', {
      _context: 'Input placeholder text for customer step in service modal.',
    }),
    emailError: t('Enter a valid email address', {
      _context: 'Error message for customer step in service modal.',
    }),
    contactNumber: t('Contact number', {
      _context: 'Input label for customer step in service modal.',
    }),
    customerGroup: t('Customer group', {
      _context: 'Input label for customer step in service modal.',
    }),
    dottMessage: t(
      `Add an email to speed up the process of contacting customers for pickup. Lightspeed Retail will automatically contact customers when their service is completed.`,
      {
        _context: 'Dott message for customer step in service modal.',
      }
    ),
  }

  const allCustomerGroups: PartialCustomerGroup[] = useCustomerGroups()

  return (
    <>
      <div className="vd-g-row">
        <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
          <div className="vd-text-label vd-mb2 vd-mt2">
            {addNewCustomerCopies.firstName}
          </div>
          <div className="vd-value">
            <input
              className="vd-input"
              placeholder={addNewCustomerCopies.firstNamePlaceholder}
              value={newCustomer?.firstName || ''}
              onChange={onChange}
              name={CUSTOMER_NAME_FIELD.FIRSTNAME}
            />
          </div>
        </div>
        <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
          <div className="vd-text-label vd-mb2 vd-mt2">
            {addNewCustomerCopies.lastName}
          </div>
          <div className="vd-value">
            <input
              className="vd-input"
              placeholder={addNewCustomerCopies.lastNamePlaceholder}
              value={newCustomer?.lastName || ''}
              onChange={onChange}
              name={CUSTOMER_NAME_FIELD.LASTNAME}
            />
          </div>
        </div>
      </div>
      <div className="vd-g-row vd-mt4">
        <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
          <div className="vd-text-label vd-mb2 vd-mt2">
            {addNewCustomerCopies.email}
          </div>
          <div className="vd-value">
            <input
              className={classNames('vd-input', {
                'vd-input--error': isInvalidEmail(newCustomer),
              })}
              placeholder={addNewCustomerCopies.emailPlaceholder}
              value={newCustomer?.email || ''}
              onChange={onChange}
              name={CONTACT_OPTIONS.EMAIL}
            />
          </div>
          {isInvalidEmail(newCustomer) && (
            <ErrorMessage>{addNewCustomerCopies.emailError}</ErrorMessage>
          )}
        </div>
        <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
          <div className="vd-text-label vd-mb2 vd-mt2">
            {addNewCustomerCopies.contactNumber}
          </div>
          <ContactNumber
            customer={newCustomer}
            onChange={onChange}
            defaultOption={getDefaultContactOption(newCustomer)}
          />
        </div>
      </div>
      <div className="vd-g-row vd-mt4">
        <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
          <div className="vd-text-label vd-mb2 vd-mt2">
            {addNewCustomerCopies.customerGroup}
          </div>
          <div className="vd-value">
            <CustomerGroupsDropdown
              onContactChange={onChange}
              customerGroups={allCustomerGroups}
            />
          </div>
        </div>
      </div>
      {!newCustomer?.email && (
        <DottMessage>{addNewCustomerCopies.dottMessage}</DottMessage>
      )}
    </>
  )
}
